import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { SwapButton } from '@root/entities/trade-type/ui'

import {
  instantBuyQuickOptionsAtom,
  reatomInstantBuy,
} from '../../atoms/reatom-instant-buy'
import { InstantTradeQuickOptionsEditButton } from '../instant-trade-buttons-edit-button'
import { InstantTradeBuyButtonsForm } from '../instant-trade-buy-buttons-form'

import { InstantTradeQuickButtonBuy } from './buy/instant-trade-quick-button-buy'
import { InstantTradeSwapInput } from './common/instant-trade-swap-input'
import { InstantTradeBuyQuote } from './instant-trade-buy-quote'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
}

export const InstantTradeSwapBuy: React.FC<Props> = ({ pair }) => {
  const { t } = useTranslation()

  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomInstantBuy({ pair }), [pair])
  const { amountInputModel } = model

  const [amountRaw] = useAtom(amountInputModel.amountRawAtom)
  const [validationError] = useAtom(model.validationAtom)
  const setAmountRaw = useAction(amountInputModel.setAmountRawAction)
  const executeBuy = useAction(model.buyAction)
  const [instantBuyQuickOptions] = useAtom(instantBuyQuickOptionsAtom)
  const [showEditQuickOptionsForm, setShowEditQuickOptionsForm] =
    useState(false)
  const initialize = useAction(model.initialize)

  useEffect(() => {
    const dispose = initialize()
    return () => {
      dispose()
    }
  }, [initialize, model])

  const renderQuickOptions = () =>
    showEditQuickOptionsForm ? (
      <InstantTradeBuyButtonsForm
        onDone={() => setShowEditQuickOptionsForm(false)}
      />
    ) : (
      <QuickOptions>
        <InstantTradeQuickOptionsEditButton
          type="button"
          state="edit"
          onClick={() => setShowEditQuickOptionsForm(true)}
        />
        {instantBuyQuickOptions.map((it, index) => (
          <InstantTradeQuickButtonBuy model={model} amount={it} key={index} />
        ))}
      </QuickOptions>
    )

  return (
    <Root>
      {renderQuickOptions()}

      <AmountSection>
        <Group>
          <InstantTradeSwapInput
            currency={pair.base}
            value={amountRaw}
            onChange={(value) => setAmountRaw(value)}
          />
          <SwapButton
            swapDirection="buy"
            onClick={() => executeBuy()}
            disabled={!!validationError}
            busy={false}
          >
            {t('trading.buy')}
          </SwapButton>
        </Group>

        <InstantTradeBuyQuote pair={pair} model={model} />
      </AmountSection>
    </Root>
  )
}

const Root = styled.div`
  gap: 8px;

  display: grid;
  overflow: hidden;
  align-items: center;
  grid-template-columns: minmax(0, 1fr);
`

const AmountSection = styled.div`
  display: grid;
  gap: 4px;
`

const QuickOptions = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 1fr 1fr 1fr;
  grid-auto-flow: column;
  gap: 2px;
`

const Group = styled.div`
  display: grid;
  grid-template-columns: 1fr 67px;
  gap: 4px;
`
